/**
 * Image Carousel Component
 * Version 3.9.0: Auto-sliding image carousel for home page
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { safeHref } from '../../utils/safeHref.js';
import './ImageCarousel.css';

function ImageCarousel({ images, autoPlayInterval = 5000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false); // Pause auto-play when user manually navigates
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    setIsAutoPlaying(false);
  };

  const handleImageClick = () => {
    const image = images[currentIndex];
    // F-40 : chemin interne → routeur ; https → navigation ; tout autre schéma
    // (javascript:, data:, http:…) est ignoré, y compris sur les lignes anciennes.
    const link = safeHref(image?.link_url);
    if (!link) return;
    if (link.internal) {
      navigate(link.href);
    } else {
      window.location.assign(link.href);
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className="carousel-empty">
        <p>Aucune image disponible</p>
      </div>
    );
  }

  const currentImage = images[currentIndex];

  return (
    <div 
      className="image-carousel"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Main Carousel */}
      <div className="carousel-container">
        {/* Previous Button */}
        {images.length > 1 && (
          <button
            className="carousel-button carousel-button-prev"
            onClick={goToPrevious}
            aria-label="Image précédente"
          >
            ‹
          </button>
        )}

        {/* Carousel Slides */}
        <div className="carousel-slides">
          {images.map((image, index) => (
            <div
              key={image.id || index}
              className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
              onClick={handleImageClick}
              style={{
                cursor: image.link_url ? 'pointer' : 'default'
              }}
            >
              <picture>
                {image.image_url_mobile && (
                  <source
                    media="(max-width: 768px)"
                    srcSet={image.image_url_mobile}
                  />
                )}
                <img
                  src={image.image_url}
                  alt={image.title || ''}
                  className="carousel-image"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchpriority={index === 0 ? 'high' : 'auto'}
                />
              </picture>
            </div>
          ))}
        </div>

        {/* Caption synced to active slide (not duplicated per slide) */}
        {(currentImage?.title || currentImage?.description) && (
          <div
            className="carousel-caption"
            key={currentImage.id || currentIndex}
            aria-live="polite"
          >
            {currentImage.title && <h2>{currentImage.title}</h2>}
            {currentImage.description && <p>{currentImage.description}</p>}
          </div>
        )}

        {/* Next Button */}
        {images.length > 1 && (
          <button
            className="carousel-button carousel-button-next"
            onClick={goToNext}
            aria-label="Image suivante"
          >
            ›
          </button>
        )}
      </div>

      {/* Dots Indicators */}
      {images.length > 1 && (
        <div className="carousel-indicators">
          {images.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      image_url: PropTypes.string.isRequired,
      image_url_mobile: PropTypes.string,
      link_url: PropTypes.string
    })
  ).isRequired,
  autoPlayInterval: PropTypes.number
};

export default ImageCarousel;
